import type { HTMLAttributes } from 'react'
import { cx } from './cx'
import './tag.css'

export type TagTone =
  | 'accent' | 'neutral' | 'danger' | 'warn' | 'outline'
  | 'sev-critical' | 'sev-high' | 'sev-medium' | 'sev-low'
export type Severity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Good'

/**
 * Severity is the product's most repeated signal, so the colour decision lives here once
 * rather than at every call site.
 *
 * Every level now has its OWN tone. Medium and Low both mapped to `neutral`, which made them
 * the same badge -- not similar, identical -- and Critical and High were two tints of one rose.
 * The four fills are separated by lightness rather than by hue alone, so the order still reads
 * when the colour does not: printed, on a greyscale screen, or to a reader with a red-green
 * deficiency. Measurements are in the severity block of tokens.css.
 */
const SEVERITY_TONE: Record<Severity, TagTone> = {
  Critical: 'sev-critical',
  High: 'sev-high',
  Medium: 'sev-medium',
  Low: 'sev-low',
  Good: 'accent',
}

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: TagTone
  /** Sets the tone from a severity. Overrides `tone` when both are given. */
  severity?: Severity
}

export function Tag({ tone = 'neutral', severity, className, children, ...rest }: TagProps) {
  const resolved = severity ? SEVERITY_TONE[severity] : tone
  return (
    <span className={cx('tag', `tag-${resolved}`, className)} {...rest}>
      {children ?? severity}
    </span>
  )
}
